// Checkbox (native input, so forms, labels and keyboard work for free; supports the indeterminate "some selected"
// state the table header needs) and Switch (iOS Toggle, tinted with the app's orange like GroundTeamApp's .tint).

import { Check, Minus } from 'lucide-react';
import {
  useCallback,
  useId,
  type ChangeEvent,
  type InputHTMLAttributes,
  type MouseEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { cn } from './cn';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'size'> {
  checked?: boolean;
  /** Shows a dash: part of a group is selected. The input's `checked` should be false meanwhile. */
  indeterminate?: boolean;
  onCheckedChange?: (checked: boolean, event: ChangeEvent<HTMLInputElement>) => void;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /** Visible label to the right. Without one, pass `aria-label`. */
  label?: ReactNode;
  description?: ReactNode;
  size?: 'sm' | 'md';
  ref?: Ref<HTMLInputElement>;
}

export function Checkbox({
  checked,
  indeterminate = false,
  onCheckedChange,
  onChange,
  label,
  description,
  size = 'md',
  disabled,
  className,
  id,
  ref,
  ...rest
}: CheckboxProps) {
  const autoId = useId();
  const inputId = id ?? `cb${autoId}`;
  const descriptionId = description ? `${inputId}-desc` : undefined;
  // `indeterminate` is a DOM property, not an attribute; set it whenever the node or the flag changes.
  const setNode = useCallback(
    (node: HTMLInputElement | null) => {
      if (node) node.indeterminate = indeterminate;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as { current: HTMLInputElement | null }).current = node;
    },
    [indeterminate, ref],
  );
  const box = size === 'sm' ? 'size-4 rounded-[5px]' : 'size-[18px] rounded-[6px]';
  const control = (
    <span className={cn('relative inline-flex shrink-0', box)}>
      <input
        ref={setNode}
        id={inputId}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-checked={indeterminate ? 'mixed' : undefined}
        aria-describedby={descriptionId}
        onChange={(e) => {
          onChange?.(e);
          onCheckedChange?.(e.target.checked, e);
        }}
        {...rest}
        className={cn(
          'peer absolute inset-0 m-0 cursor-pointer appearance-none border border-line-strong bg-surface',
          'transition-colors checked:border-primary checked:bg-primary indeterminate:border-primary',
          'indeterminate:bg-primary hover:border-[color-mix(in_srgb,var(--gt-fg)_35%,transparent)]',
          'checked:hover:border-primary-hover checked:hover:bg-primary-hover',
          'disabled:cursor-not-allowed disabled:opacity-50',
          box,
        )}
      />
      <Check
        aria-hidden
        strokeWidth={3.25}
        className="pointer-events-none absolute inset-0 m-auto size-[70%] text-primary-contrast opacity-0 peer-checked:opacity-100 peer-indeterminate:opacity-0"
      />
      <Minus
        aria-hidden
        strokeWidth={3.25}
        className="pointer-events-none absolute inset-0 m-auto size-[70%] text-primary-contrast opacity-0 peer-indeterminate:opacity-100"
      />
    </span>
  );
  if (label == null && description == null) {
    return <span className={cn('inline-flex', className)}>{control}</span>;
  }
  return (
    <div className={cn('flex items-start gap-2.5', disabled && 'opacity-60', className)}>
      <span className="flex h-5 items-center">{control}</span>
      <div className="min-w-0">
        {label != null && (
          <label htmlFor={inputId} className={cn('text-sm text-fg', disabled ? 'cursor-not-allowed' : 'cursor-pointer')}>
            {label}
          </label>
        )}
        {description != null && (
          <p id={descriptionId} className="text-xs text-fg-muted">
            {description}
          </p>
        )}
      </div>
    </div>
  );
}

export interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Visible label to the left (settings rows). Without one, pass `aria-label`. */
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  size?: 'sm' | 'md';
  id?: string;
  name?: string;
  'aria-label'?: string;
  className?: string;
  ref?: Ref<HTMLButtonElement>;
}

/** iOS-style on/off switch: role="switch", Space/Enter toggle (native button behavior). */
export function Switch({
  checked,
  onCheckedChange,
  label,
  description,
  disabled,
  size = 'md',
  id,
  name,
  className,
  ref,
  ...aria
}: SwitchProps) {
  const autoId = useId();
  const switchId = id ?? `sw${autoId}`;
  const labelId = label != null ? `${switchId}-label` : undefined;
  const descriptionId = description != null ? `${switchId}-desc` : undefined;
  const track = size === 'sm' ? 'h-5 w-8' : 'h-[26px] w-[44px]';
  const knob = size === 'sm' ? 'size-4' : 'size-[22px]';
  const travel = size === 'sm' ? 'translate-x-3' : 'translate-x-[18px]';
  const button = (
    <button
      ref={ref}
      id={switchId}
      type="button"
      role="switch"
      name={name}
      aria-checked={checked}
      aria-labelledby={labelId}
      aria-describedby={descriptionId}
      aria-label={aria['aria-label']}
      disabled={disabled}
      onClick={(e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        onCheckedChange(!checked);
      }}
      className={cn(
        'relative inline-flex shrink-0 items-center rounded-full p-0.5 transition-colors duration-200',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-primary' : 'bg-surface-3 shadow-[inset_0_0_0_1px_var(--gt-line-strong)]',
        track,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'rounded-full bg-white shadow-[0_2px_4px_rgb(0_0_0/0.18),0_0_0_0.5px_rgb(0_0_0/0.04)]',
          'transition-transform duration-200 ease-[cubic-bezier(0.3,1.3,0.5,1)]',
          knob,
          checked ? travel : 'translate-x-0',
        )}
      />
    </button>
  );
  if (label == null && description == null) {
    return <span className={cn('inline-flex', className)}>{button}</span>;
  }
  return (
    <div className={cn('flex items-center justify-between gap-4', disabled && 'opacity-60', className)}>
      <div className="min-w-0">
        {label != null && (
          <label
            id={labelId}
            htmlFor={switchId}
            className={cn('text-sm font-medium text-fg', disabled ? 'cursor-not-allowed' : 'cursor-pointer')}
          >
            {label}
          </label>
        )}
        {description != null && (
          <p id={descriptionId} className="text-xs text-fg-muted">
            {description}
          </p>
        )}
      </div>
      {button}
    </div>
  );
}
